import React, { useEffect, useMemo, useState } from 'react';
import { MultiSelect, type MultiSelectProps } from 'primereact/multiselect';
import { Heading } from '../../tokens/Typography/Heading';
import { LiliTextInput } from '../LiliTextInput';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';

interface ILiliMultiSelectProps extends Omit<MultiSelectProps, 'onChange'> {
  maxSelectedLabels: number;
  items: { title: string; value: string }[];
  onChange: (selectedValues: string[]) => void;
  defaultValue?: string[];
  closeIcon?: React.ReactNode;
  activeIcon?: React.ReactNode;
  placeholder: string;
}

export const LiliMultiSelect = (props: ILiliMultiSelectProps) => {
  const [filter, setFilter] = useState('');

  const { maxSelectedLabels, items, onChange, defaultValue, activeIcon, ...otherProps } = props;

  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const getItemIndex = (itemValue: string) => items.findIndex((i) => i.value === itemValue);

  const canSelectMoreItems = useMemo(
    () =>
      !(maxSelectedLabels === selectedValues.length || maxSelectedLabels < selectedValues.length),
    [selectedValues]
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => item.title.toLowerCase().includes(filter.toLowerCase()));
  }, [filter, items]);

  useEffect(() => {
    if (defaultValue) {
      setSelectedValues(defaultValue);
    }
  }, []);

  const renderItemTemplate = (option: { title: string; value: string }) => {
    const isSelected = selectedValues.includes(option.value);

    return (
      <div className='absolute bg-surface-primary-bw-00 flex items-center border-b border-outline-primary-bw-03 py-[6px] px-[12px] inset-0'>
        <div
          className={`${isSelected ? 'bg-surface-brand-action-00' : ''} w-full relative rounded-[6px] flex items-center group hover:bg-outline-primary-bw-02 px-[12px] py-[6px]`}>
          <Heading.H3
            className={`${isSelected ? 'text-text-brand-primary' : 'text-text-main-secondary'} group-hover:text-text-brand-primary text-[14px]`}>
            {option.title}
          </Heading.H3>

          {isSelected && activeIcon && (
            <div className='absolute right-[6px] top-1/2 transform -translate-y-1/2'>
              {activeIcon}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleClearChip = (event: React.MouseEvent<HTMLButtonElement>, itemValue: string) => {
    event.stopPropagation();
    const updatedSelectedItems = selectedValues.filter((item) => item !== itemValue);
    setSelectedValues(updatedSelectedItems);
    onChange(updatedSelectedItems);
  };

  const activeSelectedItemTemplate = (itemValue: string) => (
    <span className='flex cursor-default w-fit bg-surface-brand-action-00 items-center gap-[6px] py-[8px] px-[12px] rounded-[20px]'>
      <Heading.H3 className='text-[14px] text-text-main-primary'>
        {items[getItemIndex(itemValue)].title}
      </Heading.H3>
      <button
        onClick={(event) => handleClearChip(event, itemValue)}
        className='w-fit'
        type='button'>
        {props.closeIcon ? (
          props.closeIcon
        ) : (
          <ClearIcon className='w-[18px] stroke-text-brand-primary text-text-brand-primary' />
        )}
      </button>
    </span>
  );

  return (
    <div className='w-full h-[70px]' id='LiliMultiSelectWrapper'>
      <MultiSelect
        filter
        filterTemplate={
          <div className='h-[80px] w-full'>
            <div className='absolute inset-0 bg-surface-primary-bw-00 px-[16px] py-[14px]'>
              <LiliTextInput
                title='Search'
                value={filter}
                onChange={(value) => setFilter(value)}
                id='LiliMultiSelectFilter'
              />
            </div>
          </div>
        }
        selectedItemTemplate={(item) =>
          selectedValues.length > 0 ? activeSelectedItemTemplate(item) : null
        }
        closeIcon={<span className='hidden' />}
        itemTemplate={(optionValue) => renderItemTemplate(optionValue)}
        itemClassName={`relative h-[40px] bg-surface-primary-bw-00 ${canSelectMoreItems ? '' : 'pointer-events-none'}`}
        panelClassName='border border-outline-primary-bw-03 rounded-[8px] overflow-hidden'
        value={selectedValues}
        optionLabel='title'
        optionValue='value'
        onChange={(e) => {
          setSelectedValues(e.value);
          onChange(e.value);
        }}
        options={filteredItems}
        maxSelectedLabels={3}
        className='pl-[16px] text-text-main-tertiary pr-[12px] py-[14px] outline-none shadow-none w-full border border-outline-primary-bw-04 rounded-[8px] h-[54px] flex items-center'
        {...otherProps}
      />
    </div>
  );
};
